import { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection, doc, onSnapshot, query, where, updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import {
  LOAN_STATUS, OPEN_LOAN_STATUSES, compareSlots, isSlotAvailable,
  borrowSlot, declareReturn as declareReturnFn, toDate,
} from "../lib/loans";

/**
 * Logique d'emprunt partagée par toutes les pages équipement.
 * Chaque page garde son UI ; elle ne fait que lire ce hook.
 *
 * @param slotCollection  ex: "calculator_slots"
 * @param fallbackMinutes durée si la catégorie n'a pas de loanMinutes (null = illimité)
 * @param pickSlot        (availableSlots) => slots triés par préférence
 */
export function useLoan(slotCollection, { fallbackMinutes = null, pickSlot } = {}) {
  const [user, setUser]           = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(!!auth.currentUser);
  const [slots, setSlots]         = useState([]);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [category, setCategory]   = useState(null);
  const [loan, setLoan]           = useState(null);
  const [loanLoaded, setLoanLoaded] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [justClosed, setJustClosed] = useState(null); // "returned" | "closed"
  const [now, setNow]             = useState(Date.now());
  const prevLoanRef = useRef(null);

  // Auth — évite le bug "auth.currentUser est null au premier rendu"
  useEffect(() => onAuthStateChanged(auth, u => { setUser(u); setAuthReady(true); }), []);

  // Slots
  useEffect(() => {
    const unsub = onSnapshot(collection(db, slotCollection), snap => {
      setSlots(snap.docs.map(d => ({ docId: d.id, ...d.data() })).sort(compareSlots));
      setSlotsLoaded(true);
    }, err => { console.error(err); setSlotsLoaded(true); });
    return unsub;
  }, [slotCollection]);

  // Catégorie (pour loanMinutes, titre, prix)
  useEffect(() => {
    const q = query(collection(db, "equipment_categories"), where("collection", "==", slotCollection));
    return onSnapshot(q, snap => {
      const d = snap.docs[0];
      setCategory(d ? { id: d.id, ...d.data() } : null);
    }, console.error);
  }, [slotCollection]);

  // Emprunt ouvert du membre dans cette catégorie
  // (userId == + status in → pas d'index composite nécessaire ; filtre catégorie côté client)
  useEffect(() => {
    if (!user) { setLoan(null); setLoanLoaded(authReady); return; }
    const q = query(
      collection(db, "loans"),
      where("userId", "==", user.uid),
      where("status", "in", OPEN_LOAN_STATUSES),
    );
    return onSnapshot(q, snap => {
      const mine = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => l.slotCollection === slotCollection);
      setLoan(mine[0] || null);
      setLoanLoaded(true);
    }, err => { console.error(err); setLoanLoaded(true); });
  }, [user, authReady, slotCollection]);

  // Détecte la clôture de l'emprunt par l'accueil → écran "Merci"
  useEffect(() => {
    const prev = prevLoanRef.current;
    if (prev && !loan) {
      setJustClosed(prev.status === LOAN_STATUS.RETURN_PENDING ? "returned" : "closed");
      const t = setTimeout(() => setJustClosed(null), 4000);
      prevLoanRef.current = loan;
      return () => clearTimeout(t);
    }
    prevLoanRef.current = loan;
  }, [loan]);

  // Horloge (uniquement quand un décompte est affiché)
  const dueAt = toDate(loan?.dueAt);
  useEffect(() => {
    if (!dueAt || loan?.status !== LOAN_STATUS.ACTIVE) return;
    setNow(Date.now());
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [dueAt?.getTime(), loan?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const loanMinutes = category?.loanMinutes ?? fallbackMinutes ?? null;
  const availableSlots = useMemo(() => slots.filter(isSlotAvailable), [slots]);
  const loanSlot = loan ? slots.find(s => s.docId === loan.slotDocId) || null : null;
  const secondsLeft = dueAt ? Math.max(0, Math.round((dueAt.getTime() - now) / 1000)) : null;
  const overdue = !!dueAt && loan?.status === LOAN_STATUS.ACTIVE && now > dueAt.getTime();
  const totalSeconds = (loan?.loanMinutes ?? loanMinutes ?? 0) * 60;

  const borrow = async () => {
    if (!user) { setError("Connectez-vous pour emprunter."); return false; }
    if (loan) return false;
    setBusy(true); setError(null);
    try {
      const ordered = pickSlot ? pickSlot(availableSlots) : availableSlots;
      const res = await borrowSlot({ slotCollection, candidates: ordered, user, category, loanMinutes });
      if (!res) { setError("none"); return false; }
      return true;
    } catch (e) {
      console.error(e); setError("Une erreur est survenue. Réessayez."); return false;
    } finally { setBusy(false); }
  };

  const reportCondition = async (condition) => {
    if (!loan) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "loans", loan.id), { condition });
      if (condition === "defective") {
        await updateDoc(doc(db, slotCollection, loan.slotDocId), { reportedDefective: true });
      }
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const declareReturn = async () => {
    if (!loan) return;
    setBusy(true);
    try { await declareReturnFn(loan); }
    catch (e) { console.error(e); setError("Impossible d'enregistrer le retour. Réessayez."); }
    finally { setBusy(false); }
  };

  return {
    user, authReady, category, loanMinutes,
    slots, slotsLoaded, availableSlots,
    loan, loanSlot, loanLoaded,
    secondsLeft, totalSeconds, overdue,
    busy, error, clearError: () => setError(null),
    justClosed,
    borrow, reportCondition, declareReturn,
    ready: slotsLoaded && loanLoaded,
  };
}
